import axios from "axios";
import { BASE_URL } from "../constant/constant";
import { toastError } from "../utils/toastUtils";
import authService from "./authService";

// Parse organization and app ID from URL
const parseAppUrl = () => {
  const path = window.location.pathname;
  const match = path.match(/\/organization\/([^/]+)\/app\/([^/]+)/);
  if (match) {
    return {
      orgId: match[1],
      appId: match[2]
    };
  }
  return {
    orgId: "",
    appId: ""
  };
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json"
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getLocalToken();
    const appId = parseAppUrl().appId;

    if (token) {
      config.headers.Authorization = `${token}`;
    }

    if (appId) {
      config.headers["Zino-App-Id"] = appId;
    }

    return config;
  },
  (error) => {
    console.error("[Request Interceptor Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response?.data ? response.data : response;
  },
  (error) => {
    // Global handler for 401 Unauthorized
    if (error?.response?.status === 401 || error?.response?.data?.code === 401) {
      authService.clearToken?.();
      window.location.href = "/";
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// Enhanced error handler
const handleApiError = (error, context = "API Error", showToast = true) => {
  // 1. Handle Cancelled Requests
  if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
    console.warn(`[${context}] Request cancelled.`);
    return { success: false, isCanceled: true };
  }
  console.log("error", error);

  // Handle network errors
  if (!error?.response) {
    console.error(`[${context}] Network Error - No response received`);
    if (showToast) {
      if (typeof error === "string") {
        toastError(error);
      } else {
        toastError("Network error. Please check your connection.");
      }
    }
    return { networkError: true };
  }

  // Ignore 423 errors in generic handler since interceptor handles the redirect
  if (error?.response?.status === 423) {
    return { cancelled: true };
  }

  // Detailed logging
  console.groupCollapsed(`[${context}] ❌ API Error`);
  console.error("📍 URL:", error?.config?.url);
  console.error("💥 Error Object:", error);
  if (error.response) {
    console.error("📨 Response Data:", error.response.data);
    console.error("🔢 Status:", error.response.status);
  } else if (error.request) {
    console.error("📡 No Response Received (Request was sent):", error.request);
  }
  console.groupEnd();

  // 3. Determine Error Message
  let errorMessage = "Something went wrong";

  // --- SCENARIO A: No Response (Network / Timeout / CORS) ---
  if (!error?.response) {
    if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
      errorMessage = "Request timed out. Please try again.";
    } else if (error?.message === "Network Error") {
      errorMessage = "Network error. Please check your internet connection.";
    } else {
      errorMessage = "Unable to connect to the server.";
    }
  }

  // --- SCENARIO B: Server Responded with Error (4xx, 5xx) ---
  else {
    const responseData = error.response.data;

    if (responseData?.message) {
      errorMessage = responseData.message;
    } else if (typeof responseData?.error === "string") {
      errorMessage = responseData.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
  }

  // 4. Show Toast
  if (showToast) {
    toastError(errorMessage);
  }

  // 5. Return Safe Object
  return {
    success: false,
    message: errorMessage,
    status: error?.response?.status || 0,
    isNetworkError: !error?.response
  };
};

class APIService {
  constructor(axiosInstance) {
    this.client = axiosInstance;
  }

  // GET request
  async get(url, config = {}) {
    try {
      return await this.client.get(url, config);
    } catch (error) {
      handleApiError(error, `GET ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // POST request
  async post(url, data = {}, config = {}) {
    try {
      return await this.client.post(url, data, config);
    } catch (error) {
      handleApiError(error, `POST ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // PUT request (full update)
  async put(url, data = {}, config = {}) {
    try {
      return await this.client.put(url, data, config);
    } catch (error) {
      handleApiError(error, `PUT ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // PATCH request (partial update)
  async patch(url, data = {}, config = {}) {
    try {
      return await this.client.patch(url, data, config);
    } catch (error) {
      handleApiError(error, `PATCH ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // DELETE request
  async delete(url, config = {}) {
    try {
      return await this.client.delete(url, config);
    } catch (error) {
      handleApiError(error, `DELETE ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // GET with pagination
  async getPaginated(url, page = 1, limit = 10, config = {}) {
    return this.get(url, {
      ...config,
      params: { ...config.params, page, limit }
    });
  }

  // Upload file
  async uploadFile(url, file, onProgress, config = {}) {
    const formData = new FormData();
    formData.append("file", file);

    return this.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data"
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
  }

  // Download file
  async downloadFile(url, filename, config = {}) {
    try {
      const response = await this.client.get(url, {
        ...config,
        responseType: "blob"
      });

      const blob = new Blob([response]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename || "download";
      link.click();
      window.URL.revokeObjectURL(link.href);

      return response;
    } catch (error) {
      handleApiError(error, `DOWNLOAD ${url}`, config.showToast !== false);
      throw error;
    }
  }

  // Batch requests (parallel)
  async batchGet(urls, config = {}) {
    try {
      const requests = urls.map((url) => this.get(url, { ...config, showToast: false }));
      return await Promise.all(requests);
    } catch (error) {
      handleApiError(error, "BATCH GET", config.showToast !== false);
      throw error;
    }
  }

  // Sequential requests (one after another)
  async sequentialRequests(requests) {
    const results = [];
    for (const request of requests) {
      try {
        const result = await request();
        results.push(result);
      } catch (error) {
        console.error("Sequential request failed:", error);
        results.push(null);
      }
    }
    return results;
  }

  // Retry mechanism for specific requests
  async retryRequest(requestFn, maxRetries = 3, delay = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        console.warn(`Retry attempt ${attempt}/${maxRetries} failed. Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
  }
}

// Create and export service instance
const apiService = new APIService(axiosInstance);

export default axiosInstance;
export { handleApiError, apiService, parseAppUrl };
